// =============================================================================
// property.types.ts
// Sistema de Arrendamiento — DBrent
// Fuente de verdad: leasing.*, catalog.*, internal.*, analytics.*, public.profiles
//
// NOTA IMPORTANTE
// - Este archivo refleja el MODELO V2 deseado.
// - Incluye la taxonomía completa objetivo de tipos/subtipos.
// - El frontend debe mantenerse alineado con catalog.property_types y
//   catalog.property_subtypes como fuente operativa de verdad.
// =============================================================================

// -----------------------------------------------------------------------------
// SECTION 0 — PRIMITIVOS BASE
// -----------------------------------------------------------------------------

export type UUID = string;
export type ISODateString = string; // YYYY-MM-DD
export type ISODateTimeString = string; // timestamptz serializado
export type TimeString = string; // HH:mm

// -----------------------------------------------------------------------------
// SECTION 1 — ENUMS / VALUES (alineados a PostgreSQL)
// -----------------------------------------------------------------------------

export const PUBLICATION_STATUSES = [
  'draft',
  'internal_review',
  'ready_to_publish',
  'published',
  'unpublished',
  'archived',
] as const;
export type PublicationStatus = (typeof PUBLICATION_STATUSES)[number];

export const AVAILABILITY_STATUSES = [
  'coming_soon',
  'available',
  'hold',
  'application_in_progress',
  'leased',
  'inactive',
] as const;
export type AvailabilityStatus = (typeof AVAILABILITY_STATUSES)[number];

export const MAINTENANCE_SCHEMES = [
  'unknown',
  'included',
  'separate_amount',
  'not_applicable',
] as const;
export type MaintenanceScheme = (typeof MAINTENANCE_SCHEMES)[number];

export const FURNISHING_LEVELS = [
  'unfurnished',
  'semi_furnished',
  'furnished',
  'both',
] as const;
export type FurnishingLevel = (typeof FURNISHING_LEVELS)[number];

export const PETS_POLICIES = [
  'allowed',
  'not_allowed',
  'case_by_case',
] as const;
export type PetsPolicy = (typeof PETS_POLICIES)[number];

export const PARKING_TYPES = [
  'independiente',
  'en_fila',
  'no_aplica',
] as const;
export type ParkingType = (typeof PARKING_TYPES)[number];

export const PROPERTY_CONDITIONS = [
  'a_estrenar',
  'excelente',
  'bueno',
  'regular',
  'malo',
] as const;
export type PropertyCondition = (typeof PROPERTY_CONDITIONS)[number];

export const DISPLAY_PRECISIONS = [
  'exact',
  'approx_block',
  'approx_colonia',
] as const;
export type DisplayPrecision = (typeof DISPLAY_PRECISIONS)[number];

export const MEDIA_KINDS = [
  'image',
  'floorplan',
  'video',
  'virtual_tour',
] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_VISIBILITIES = ['public', 'internal'] as const;
export type MediaVisibility = (typeof MEDIA_VISIBILITIES)[number];

export const FEATURE_GROUPS = [
  'amenity',
  'included_service',
  'highlight_feature',
] as const;
export type FeatureGroup = (typeof FEATURE_GROUPS)[number];

export const PROFILE_ROLES = [
  'pending',
  'admin',
  'propietario',
  'inquilino',
  'proveedor',
  'agent',
] as const;
export type UserRole = (typeof PROFILE_ROLES)[number];

export const INTERNAL_OPERATOR_ROLES = ['admin', 'agent'] as const;
export type InternalOperatorRole = (typeof INTERNAL_OPERATOR_ROLES)[number];

export const DASHBOARD_SORT_OPTIONS = [
  'updated_desc',
  'created_desc',
  'price_desc',
  'price_asc',
  'score_desc',
  'views_desc',
] as const;
export type DashboardSortOption = (typeof DASHBOARD_SORT_OPTIONS)[number];

// -----------------------------------------------------------------------------
// SECTION 2 — CATÁLOGO DE TIPOS / SUBTIPOS / FAMILIAS
// Fuente de verdad del frontend para formularios.
// -----------------------------------------------------------------------------

export const PROPERTY_TYPE_CATALOG = {
  departamento: {
    label: 'Departamento',
    family: 'residential',
    subtypes: ['estandar', 'estudio', 'loft', 'penthouse', 'garden_house', 'duplex'],
  },
  casa: {
    label: 'Casa',
    family: 'residential',
    subtypes: ['casa_sola', 'casa_en_condominio', 'townhouse', 'casa_duplex'],
  },
  consultorio: {
    label: 'Consultorio',
    family: 'consulting_room',
    subtypes: ['en_torre_medica', 'en_clinica', 'independiente'],
  },
  oficina: {
    label: 'Oficina',
    family: 'office',
    subtypes: ['piso_completo', 'fraccion_de_piso', 'oficina_privada', 'espacio_flexible'],
  },
  bodega: {
    label: 'Bodega',
    family: 'industrial',
    subtypes: ['bodega_comercial', 'mini_bodega', 'bodega_logistica'],
  },
  local_comercial: {
    label: 'Local comercial',
    family: 'retail',
    subtypes: ['a_pie_de_calle', 'en_plaza', 'isla', 'food_court', 'showroom'],
  },
  nave_industrial: {
    label: 'Nave industrial',
    family: 'industrial',
    subtypes: ['nave_logistica', 'nave_manufactura', 'mini_nave', 'patio_con_bodega'],
  },
  habitacion: {
    label: 'Habitación',
    family: 'room',
    subtypes: ['individual', 'suite', 'compartida'],
  },
} as const;

export type PropertyTypeCode = keyof typeof PROPERTY_TYPE_CATALOG;

export type PropertyFamilyCode =
  (typeof PROPERTY_TYPE_CATALOG)[PropertyTypeCode]['family'];

type PropertyCatalog = typeof PROPERTY_TYPE_CATALOG;

export type PropertySubtypeCode = {
  [K in PropertyTypeCode]: PropertyCatalog[K]['subtypes'][number];
}[PropertyTypeCode];

export type PropertySubtypeCodeByType<T extends PropertyTypeCode> =
  PropertyCatalog[T]['subtypes'][number];

// -----------------------------------------------------------------------------
// SECTION 2.1 — LEGACY ACTUAL
// Útil para migraciones / dashboards de depuración.
// -----------------------------------------------------------------------------

export const LEGACY_PROPERTY_TYPE_CODES = [
  'departamento',
  'casa',
  'oficina',
  'habitacion',
  'penthouse',
  'localcomercial',
  'bodega',
  'loft',
] as const;

export type LegacyPropertyTypeCode = (typeof LEGACY_PROPERTY_TYPE_CODES)[number];

// -----------------------------------------------------------------------------
// SECTION 3 — LABEL MAPS (ES)
// -----------------------------------------------------------------------------

export const PUBLICATION_STATUS_LABELS: Record<PublicationStatus, string> = {
  draft: 'Borrador',
  internal_review: 'En revisión',
  ready_to_publish: 'Listo para publicar',
  published: 'Publicado',
  unpublished: 'Despublicado',
  archived: 'Archivado',
};

export const AVAILABILITY_STATUS_LABELS: Record<AvailabilityStatus, string> = {
  coming_soon: 'Próximamente',
  available: 'Disponible',
  hold: 'Reservado',
  application_in_progress: 'Solicitud en proceso',
  leased: 'Rentado',
  inactive: 'Inactivo',
};

export const MAINTENANCE_SCHEME_LABELS: Record<MaintenanceScheme, string> = {
  unknown: 'No especificado',
  included: 'Incluido en renta',
  separate_amount: 'Monto adicional',
  not_applicable: 'No aplica',
};

export const FURNISHING_LEVEL_LABELS: Record<FurnishingLevel, string> = {
  unfurnished: 'Sin amueblar',
  semi_furnished: 'Semi amueblado',
  furnished: 'Amueblado',
  both: 'Ambos',
};

export const PETS_POLICY_LABELS: Record<PetsPolicy, string> = {
  allowed: 'Se aceptan mascotas',
  not_allowed: 'No se aceptan mascotas',
  case_by_case: 'Depende del caso',
};

export const PARKING_TYPE_LABELS: Record<ParkingType, string> = {
  independiente: 'Independiente',
  en_fila: 'En fila',
  no_aplica: 'Sin cajón',
};

export const PROPERTY_CONDITION_LABELS: Record<PropertyCondition, string> = {
  a_estrenar: 'A estrenar',
  excelente: 'Excelente',
  bueno: 'Bueno',
  regular: 'Regular',
  malo: 'Requiere reparaciones',
};

export const DISPLAY_PRECISION_LABELS: Record<DisplayPrecision, string> = {
  exact: 'Dirección exacta',
  approx_block: 'Aproximada (cuadra)',
  approx_colonia: 'Aproximada (colonia)',
};

export const MEDIA_KIND_LABELS: Record<MediaKind, string> = {
  image: 'Fotografía',
  floorplan: 'Plano',
  video: 'Video',
  virtual_tour: 'Tour virtual',
};

export const FEATURE_GROUP_LABELS: Record<FeatureGroup, string> = {
  amenity: 'Amenidad',
  included_service: 'Servicio incluido',
  highlight_feature: 'Característica destacada',
};

export const PROPERTY_TYPE_LABELS: Record<PropertyTypeCode, string> = {
  departamento: 'Departamento',
  casa: 'Casa',
  consultorio: 'Consultorio',
  oficina: 'Oficina',
  bodega: 'Bodega',
  local_comercial: 'Local comercial',
  nave_industrial: 'Nave industrial',
  habitacion: 'Habitación',
};

export const PROPERTY_SUBTYPE_LABELS: Record<PropertySubtypeCode, string> = {
  estandar: 'Estándar',
  estudio: 'Estudio',
  loft: 'Loft',
  penthouse: 'Penthouse',
  garden_house: 'Garden house',
  duplex: 'Dúplex',

  casa_sola: 'Casa sola',
  casa_en_condominio: 'Casa en condominio',
  townhouse: 'Townhouse',
  casa_duplex: 'Casa dúplex',

  en_torre_medica: 'En torre médica',
  en_clinica: 'En clínica',
  independiente: 'Independiente',

  piso_completo: 'Piso completo',
  fraccion_de_piso: 'Fracción de piso',
  oficina_privada: 'Oficina privada',
  espacio_flexible: 'Espacio flexible',

  bodega_comercial: 'Bodega comercial',
  mini_bodega: 'Mini bodega',
  bodega_logistica: 'Bodega logística',

  a_pie_de_calle: 'A pie de calle',
  en_plaza: 'En plaza',
  isla: 'Isla',
  food_court: 'Patio de comidas',
  showroom: 'Sala de exhibición',

  nave_logistica: 'Nave logística',
  nave_manufactura: 'Nave manufactura',
  mini_nave: 'Mini nave',
  patio_con_bodega: 'Patio con bodega',

  individual: 'Individual',
  suite: 'Suite',
  compartida: 'Compartida',
};

export const PROPERTY_FAMILY_LABELS: Record<PropertyFamilyCode, string> = {
  residential: 'Residencial',
  office: 'Oficinas',
  industrial: 'Industrial',
  retail: 'Comercial',
  consulting_room: 'Consultorios',
  room: 'Habitaciones',
};

export const DASHBOARD_SORT_LABELS: Record<DashboardSortOption, string> = {
  updated_desc: 'Más reciente',
  created_desc: 'Creación más reciente',
  price_desc: 'Mayor precio',
  price_asc: 'Menor precio',
  score_desc: 'Mayor calidad',
  views_desc: 'Más vistas',
};

export const USER_ROLE_LABELS: Record<UserRole, string> = {
  pending: 'Pendiente',
  admin: 'Administrador',
  propietario: 'Propietario',
  inquilino: 'Inquilino',
  proveedor: 'Proveedor',
  agent: 'Agente',
};

// -----------------------------------------------------------------------------
// SECTION 4 — BADGE / COLOR MAPS
// -----------------------------------------------------------------------------

export type StatusColor =
  | 'gray'
  | 'blue'
  | 'green'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'purple';

export const PUBLICATION_STATUS_COLOR: Record<PublicationStatus, StatusColor> = {
  draft: 'gray',
  internal_review: 'blue',
  ready_to_publish: 'purple',
  published: 'green',
  unpublished: 'orange',
  archived: 'red',
};

export const AVAILABILITY_STATUS_COLOR: Record<AvailabilityStatus, StatusColor> = {
  coming_soon: 'blue',
  available: 'green',
  hold: 'yellow',
  application_in_progress: 'purple',
  leased: 'gray',
  inactive: 'red',
};

// -----------------------------------------------------------------------------
// SECTION 5 — MODELO CORE V2 (alineado a tablas reales)
// -----------------------------------------------------------------------------

/** leasing.properties */
export interface Property {
  id: UUID;
  legacy_property_id: UUID | null;
  listing_code: number;

  property_type_id: number;
  property_subtype_id: number | null;

  publication_status: PublicationStatus;
  availability_status: AvailabilityStatus;

  display_title_override: string | null;
  display_title_auto: string | null;
  display_title_effective: string | null;
  slug: string | null;
  description_public: string | null;

  area_m2: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  half_bathrooms: number;
  parking_spots_count: number | null;
  parking_type: ParkingType | null;
  property_condition: PropertyCondition | null;

  is_featured: boolean;
  available_from: ISODateString | null;

  created_by: UUID | null;
  updated_by: UUID | null;
  published_by_user_id: UUID | null;

  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
  published_at: ISODateTimeString | null;
  unpublished_at: ISODateTimeString | null;
}

/** leasing.property_addresses */
export interface PropertyAddress {
  property_id: UUID;
  raw_address_text: string | null;
  street_line1: string | null;
  ext_number: string | null;
  int_number: string | null;
  colonia: string | null;
  alcaldia: string | null;
  city: string;
  state: string;
  postal_code: string | null;
  country_code: string;
  latitude: number | null;
  longitude: number | null;
  display_precision: DisplayPrecision;
  colonia_normalized: string;
  alcaldia_normalized: string;
  created_at?: ISODateTimeString;
  updated_at: ISODateTimeString;
}

/** leasing.property_rental_terms */
export interface PropertyRentalTerms {
  property_id: UUID;
  rent_amount: number | null;
  rent_amount_unfurnished: number | null;
  rent_amount_furnished: number | null;
  currency: string;
  maintenance_scheme: MaintenanceScheme;
  maintenance_amount: number | null;
  security_deposit_amount: number | null;
  furnishing_level: FurnishingLevel;
  pets_policy: PetsPolicy;
  minimum_term_months: number | null;
  created_at?: ISODateTimeString;
  updated_at?: ISODateTimeString;
}

/** leasing.property_reservation_settings */
export interface PropertyReservationSettings {
  property_id: UUID;
  is_reservable: boolean;
  reservation_amount: number | null;
  currency: string;
  created_at?: ISODateTimeString;
  updated_at?: ISODateTimeString;
}

/** internal.property_owner_links */
export interface PropertyOwnerLink {
  property_id: UUID;
  owner_profile_id: UUID | null;
  owner_request_id: UUID | null;
  relationship_status: string;
  assigned_by: UUID | null;
  assigned_at?: ISODateTimeString;
  updated_at?: ISODateTimeString;
}

/** internal.property_access_instructions */
export interface PropertyAccessInstructions {
  property_id: UUID;
  access_instructions: string | null;
  key_handover_instructions: string | null;
  parking_access_instructions: string | null;
  onsite_contact_name: string | null;
  onsite_contact_phone: string | null;
  updated_by: UUID | null;
  updated_at: ISODateTimeString;
}

/** leasing.property_visit_schedule_rules */
export interface PropertyVisitScheduleRule {
  id: number;
  property_id: UUID;
  weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  start_time: TimeString;
  end_time: TimeString;
  slot_minutes: 30 | 45 | 60 | 90 | 120;
  is_active: boolean;
  created_by: UUID | null;
  created_at?: ISODateTimeString;
  updated_at?: ISODateTimeString;
}

/** leasing.property_media */
export interface PropertyMedia {
  id: number;
  property_id: UUID;
  media_kind: MediaKind;
  visibility: MediaVisibility;
  bucket_id: string | null;
  storage_provider: string | null;
  storage_path: string | null;
  public_url: string | null;
  thumbnail_url: string | null;
  external_platform: string | null;
  external_media_id: string | null;
  sort_order: number;
  is_cover: boolean;
  is_active: boolean;
  caption: string | null;
  metadata: Record<string, unknown>;
  created_by: UUID | null;
  created_at: ISODateTimeString;
}

/** catalog.property_feature_definitions */
export interface FeatureDefinition {
  id: number;
  code: string;
  label_es: string;
  feature_group: FeatureGroup;
  is_public: boolean;
  is_filterable: boolean;
  is_title_eligible: boolean;
  title_priority: number;
  applicable_family_codes: PropertyFamilyCode[] | null;
  is_active: boolean;
  created_at?: ISODateTimeString;
}

/** leasing.property_features + catalog join */
export interface PropertyFeature {
  property_id: UUID;
  feature_id: number;
  code: string;
  label_es: string;
  feature_group: FeatureGroup;
  is_public: boolean;
  is_filterable: boolean;
  is_title_eligible: boolean;
}

/** leasing.property_publication_quality */
export interface PropertyPublicationQuality {
  property_id: UUID;
  score_pct: number;
  enrichment_score_pct: number;
  publish_ready: boolean;
  missing_core_items: string[];
  passed_core_items: string[];
  detail: Record<string, unknown>;
  computed_at: ISODateTimeString;
}

// -----------------------------------------------------------------------------
// SECTION 6 — DETALLES POR FAMILIA (alineados a tablas reales)
// -----------------------------------------------------------------------------

export interface PropertyResidentialDetails {
  property_id: UUID;
  floor_number: number | null;
  building_total_floors: number | null;
  year_built: number | null;
  has_balcony: boolean;
  has_terrace: boolean;
  has_garden: boolean;
  has_roof_garden: boolean;
  has_laundry_room: boolean;
  has_service_room: boolean;
  has_service_room_bathroom: boolean;
  has_studio_area: boolean;
  has_family_room: boolean;
  includes_white_goods: boolean;
  storage_unit_included: boolean;
  orientation: string | null;
  laundry_room_notes: string | null;
}

export interface PropertyRoomDetails {
  property_id: UUID;
  private_bathroom: boolean;
  kitchen_access: boolean;
  common_areas_access: boolean;
  internet_included: boolean;
  max_occupants: number | null;
}

export interface PropertyOfficeDetails {
  property_id: UUID;
  cubicles: number | null;
  private_offices: number | null;
  meeting_rooms: number | null;
  has_reception: boolean;
  has_coworking: boolean;
  has_air_conditioning: boolean;
  has_elevator: boolean;
  max_capacity: number | null;
  delivery_status: string | null;
  land_use: string | null;
}

export interface PropertyConsultingRoomDetails {
  property_id: UUID;
  consulting_room_type: string | null;
  waiting_room: boolean;
  has_reception: boolean;
  private_bathroom: boolean;
  internet_included: boolean;
  kitchen_access: boolean;
}

export interface PropertyRetailDetails {
  property_id: UUID;
  retail_type: string | null;
  frontage_m: number | null;
  depth_m: number | null;
  ceiling_height_m: number | null;
  allow_subdivision: boolean;
  land_use: string | null;
  has_grease_trap: boolean;
  ventilation_type: string | null;
}

export interface PropertyIndustrialDetails {
  property_id: UUID;
  clear_height_m: number | null;
  loading_ramps: number | null;
  has_dock: boolean;
  has_office_space: boolean;
  has_three_phase: boolean;
  has_rail_access: boolean;
  floor_load_capacity_kg_m2: number | null;
  electric_capacity_kva: number | null;
  water_source: string | null;
  has_cctv: boolean;
  has_perimeter_fence: boolean;
}

export interface PropertyFamilyDetailsBundle {
  residential: PropertyResidentialDetails | null;
  room: PropertyRoomDetails | null;
  office: PropertyOfficeDetails | null;
  consulting_room: PropertyConsultingRoomDetails | null;
  retail: PropertyRetailDetails | null;
  industrial: PropertyIndustrialDetails | null;
}

// -----------------------------------------------------------------------------
// SECTION 7 — DASHBOARD CARDS (alineado a RPCs separadas)
// -----------------------------------------------------------------------------

/** Item real de rpc_agent_properties_dashboard_page */
export interface PropertyDashboardCardAgent {
  property_id: UUID;
  listing_code: number;

  property_type_code: PropertyTypeCode;
  property_type_label: string;
  property_subtype_code: PropertySubtypeCode | null;
  property_subtype_label: string | null;

  display_title: string;
  slug: string | null;
  cover_image_url: string | null;

  raw_address_text: string | null;
  alcaldia: string | null;

  rent_amount: number | null;
  rent_amount_unfurnished: number | null;
  rent_amount_furnished: number | null;
  furnishing_level: FurnishingLevel | null;
  currency: string | null;
  maintenance_scheme: MaintenanceScheme | null;
  maintenance_amount: number | null;

  bedrooms: number | null;
  bathrooms: number | null;
  half_bathrooms: number | null;
  parking_spots_count: number | null;
  area_m2: number | null;

  publication_status: PublicationStatus;
  availability_status: AvailabilityStatus;
  is_featured: boolean;
  available_from: ISODateString | null;

  publication_score_pct: number;
  enrichment_score_pct: number;
  publish_ready: boolean;
  missing_core_items_count: number;
  missing_core_items_top: string[];

  views_count: number;
  favorites_count: number;

  has_price_drop: boolean;
  price_drop_pct: number | null;
  latest_price_change_at: ISODateTimeString | null;

  primary_external_link: string | null;

  created_at: ISODateTimeString;
  updated_at: ISODateTimeString;
}

/** Item real de rpc_admin_properties_dashboard_page */
export interface PropertyDashboardCardAdmin extends PropertyDashboardCardAgent {
  agent_id: UUID | null;
  agent_display_name: string | null;
  agent_avatar_url: string | null;
  is_reservable: boolean;
  reservation_amount: number | null;
}

// -----------------------------------------------------------------------------
// SECTION 8 — DASHBOARD RESPONSES
// -----------------------------------------------------------------------------

export interface PublicationStatusCount {
  status: PublicationStatus;
  count: number;
}

export interface AvailabilityStatusCount {
  status: AvailabilityStatus;
  count: number;
}

export interface DashboardAgentCount {
  agent_id: UUID | null;
  agent_display_name: string | null;
  count: number;
}

export interface AgentDashboardMeta {
  role: 'agent';
  limit: number;
  offset: number;
  total_count: number;
  page_size: number;
  total_pages: number;
  has_next_page: boolean;
  next_offset: number | null;
  publish_ready_count: number;
  discounted_count: number;
}

export interface AdminDashboardMeta {
  role: 'admin';
  limit: number;
  offset: number;
  total_count: number;
  page_size: number;
  total_pages: number;
  has_next_page: boolean;
  next_offset: number | null;
  publish_ready_count: number;
  discounted_count: number;
  reservable_count: number;
}

export interface AgentDashboardResponse {
  meta: AgentDashboardMeta;
  publication_status_counts: PublicationStatusCount[];
  availability_status_counts: AvailabilityStatusCount[];
  items: PropertyDashboardCardAgent[];
}

export interface AdminDashboardResponse {
  meta: AdminDashboardMeta;
  publication_status_counts: PublicationStatusCount[];
  availability_status_counts: AvailabilityStatusCount[];
  agent_counts: DashboardAgentCount[];
  items: PropertyDashboardCardAdmin[];
}

// -----------------------------------------------------------------------------
// SECTION 9 — DASHBOARD PARAMS
// -----------------------------------------------------------------------------

export interface AgentDashboardParams {
  p_limit?: number;
  p_offset?: number;
  p_query?: string | null;
  p_property_type_code?: PropertyTypeCode | null;
  p_alcaldia?: string | null;
  p_publication_status?: PublicationStatus | null;
  p_availability_status?: AvailabilityStatus | null;
  p_only_publish_ready?: boolean | null;
  p_only_discounted?: boolean | null;
  p_sort?: DashboardSortOption;
}

export interface AdminDashboardParams {
  p_limit?: number;
  p_offset?: number;
  p_query?: string | null;
  p_property_type_code?: PropertyTypeCode | null;
  p_alcaldia?: string | null;
  p_publication_status?: PublicationStatus | null;
  p_availability_status?: AvailabilityStatus | null;
  p_agent_id?: UUID | null;
  p_only_publish_ready?: boolean | null;
  p_only_discounted?: boolean | null;
  p_only_reservable?: boolean | null;
  p_sort?: DashboardSortOption;
}

// -----------------------------------------------------------------------------
// SECTION 10 — EDITOR / RPC ACTIONS
// -----------------------------------------------------------------------------

export interface PropertyEditorMeta {
  property_id: UUID;
  listing_code: number;
  role: InternalOperatorRole;
  can_edit: boolean;
  can_manage_reservation_settings: boolean;
  can_delete: boolean;
}

export interface PropertyEditorPropertyBlock {
  property_type_code: PropertyTypeCode;
  property_type_label: string;
  property_subtype_code: PropertySubtypeCode | null;
  property_subtype_label: string | null;
  display_title_override: string | null;
  display_title_auto: string | null;
  display_title_effective: string | null;
  slug: string | null;
  description_public: string | null;
  area_m2: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  half_bathrooms: number;
  parking_spots_count: number | null;
  parking_type: ParkingType | null;
  property_condition: PropertyCondition | null;
  is_featured: boolean;
  available_from: ISODateString | null;
  publication_status: PublicationStatus;
  availability_status: AvailabilityStatus;
}

export interface PropertyEditorMediaBundle {
  images: PropertyMedia[];
  floorplans: PropertyMedia[];
  videos: PropertyMedia[];
  virtual_tours: PropertyMedia[];
}

export interface PropertyEditorResponse {
  meta: PropertyEditorMeta;
  property: PropertyEditorPropertyBlock;
  address: Omit<PropertyAddress, 'property_id'> | null;
  rental_terms: Omit<PropertyRentalTerms, 'property_id'> | null;
  reservation_settings: Omit<PropertyReservationSettings, 'property_id'> | null;
  owner_link: Omit<PropertyOwnerLink, 'property_id'> | null;
  access_instructions: Omit<PropertyAccessInstructions, 'property_id'> | null;
  external_links: {
    primary_external_link: string | null;
  } | null;
  private_notes: {
    latest_note: string | null;
    latest_visibility: 'admin_only' | 'agent_internal' | 'ops_internal' | null;
    latest_created_at: string | null;
  } | null;
  visit_rules: Array<
    Omit<PropertyVisitScheduleRule, 'property_id' | 'created_by' | 'created_at' | 'updated_at'>
  >;
  feature_codes: string[];
  media: PropertyEditorMediaBundle;
  family_details: PropertyFamilyDetailsBundle;
  quality: PropertyPublicationQuality | null;
}

export interface PropertyCreateDraftResponse {
  property_id: UUID;
  listing_code: number;
  publication_status: 'draft';
  availability_status: 'inactive';
}

export interface PropertyCreateDraftParams {
  p_property_type_code: PropertyTypeCode;
  p_property_subtype_code?: PropertySubtypeCode | null;
}

// -----------------------------------------------------------------------------
// SECTION 11 — SAVE PAYLOADS DEL EDITOR
// -----------------------------------------------------------------------------

export interface PropertyAddressInput {
  raw_address_text?: string | null;
  street_line1?: string | null;
  ext_number?: string | null;
  int_number?: string | null;
  colonia?: string | null;
  alcaldia?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  display_precision?: DisplayPrecision | null;
}

export interface PropertyRentalTermsInput {
  rent_amount?: number | null;
  rent_amount_unfurnished?: number | null;
  rent_amount_furnished?: number | null;
  currency?: string | null;
  maintenance_scheme?: MaintenanceScheme | null;
  maintenance_amount?: number | null;
  security_deposit_amount?: number | null;
  furnishing_level?: FurnishingLevel | null;
  pets_policy?: PetsPolicy | null;
  minimum_term_months?: number | null;
}

export interface PropertyOwnerLinkInput {
  owner_profile_id?: UUID | null;
  owner_request_id?: UUID | null;
}

export interface PropertyAccessInstructionsInput {
  access_instructions?: string | null;
  key_handover_instructions?: string | null;
  parking_access_instructions?: string | null;
  onsite_contact_name?: string | null;
  onsite_contact_phone?: string | null;
}

export interface PropertyMainInput {
  property_type_code?: PropertyTypeCode | null;
  property_subtype_code?: PropertySubtypeCode | null;
  display_title_override?: string | null;
  description_public?: string | null;
  area_m2?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  half_bathrooms?: number | null;
  parking_spots_count?: number | null;
  parking_type?: ParkingType | null;
  property_condition?: PropertyCondition | null;
  is_featured?: boolean | null;
  available_from?: ISODateString | null;
}

export interface PropertyResidentialDetailsInput {
  floor_number?: number | null;
  building_total_floors?: number | null;
  year_built?: number | null;
  has_balcony?: boolean | null;
  has_terrace?: boolean | null;
  has_garden?: boolean | null;
  has_roof_garden?: boolean | null;
  has_laundry_room?: boolean | null;
  has_service_room?: boolean | null;
  has_service_room_bathroom?: boolean | null;
  has_studio_area?: boolean | null;
  has_family_room?: boolean | null;
  includes_white_goods?: boolean | null;
  storage_unit_included?: boolean | null;
  orientation?: string | null;
  laundry_room_notes?: string | null;
}

export interface PropertyRoomDetailsInput {
  private_bathroom?: boolean | null;
  kitchen_access?: boolean | null;
  common_areas_access?: boolean | null;
  internet_included?: boolean | null;
  max_occupants?: number | null;
}

export interface PropertyOfficeDetailsInput {
  cubicles?: number | null;
  private_offices?: number | null;
  meeting_rooms?: number | null;
  has_reception?: boolean | null;
  has_coworking?: boolean | null;
  has_air_conditioning?: boolean | null;
  has_elevator?: boolean | null;
  max_capacity?: number | null;
  delivery_status?: string | null;
  land_use?: string | null;
}

export interface PropertyConsultingRoomDetailsInput {
  consulting_room_type?: string | null;
  waiting_room?: boolean | null;
  has_reception?: boolean | null;
  private_bathroom?: boolean | null;
  internet_included?: boolean | null;
  kitchen_access?: boolean | null;
}

export interface PropertyRetailDetailsInput {
  retail_type?: string | null;
  frontage_m?: number | null;
  depth_m?: number | null;
  ceiling_height_m?: number | null;
  allow_subdivision?: boolean | null;
  land_use?: string | null;
  has_grease_trap?: boolean | null;
  ventilation_type?: string | null;
}

export interface PropertyIndustrialDetailsInput {
  clear_height_m?: number | null;
  loading_ramps?: number | null;
  has_dock?: boolean | null;
  has_office_space?: boolean | null;
  has_three_phase?: boolean | null;
  has_rail_access?: boolean | null;
  floor_load_capacity_kg_m2?: number | null;
  electric_capacity_kva?: number | null;
  water_source?: string | null;
  has_cctv?: boolean | null;
  has_perimeter_fence?: boolean | null;
}

export type PropertyFamilyDetailsInput =
  | PropertyResidentialDetailsInput
  | PropertyRoomDetailsInput
  | PropertyOfficeDetailsInput
  | PropertyConsultingRoomDetailsInput
  | PropertyRetailDetailsInput
  | PropertyIndustrialDetailsInput;

export interface PropertySaveMainPayload {
  property: PropertyMainInput;
  address?: PropertyAddressInput;
  rental_terms?: PropertyRentalTermsInput;
  owner_link?: PropertyOwnerLinkInput;
  access_instructions?: PropertyAccessInstructionsInput;
  family_details?: PropertyFamilyDetailsInput;
}

export interface PropertyMediaInputBase {
  bucket_id?: string | null;
  storage_path?: string | null;
  public_url?: string | null;
  sort_order?: number | null;
  metadata?: Record<string, unknown> | null;
}

export interface PropertyImageInput extends PropertyMediaInputBase {
  is_cover?: boolean | null;
}

export interface PropertyVideoInput extends PropertyMediaInputBase {
  thumbnail_url?: string | null;
  external_platform?: string | null;
  external_media_id?: string | null;
}

export interface PropertySaveMediaAndFeaturesPayload {
  feature_codes: string[];
  images?: PropertyImageInput[];
  floorplans?: PropertyMediaInputBase[];
  videos?: PropertyVideoInput[];
  virtual_tours?: PropertyMediaInputBase[];
}

export interface PropertyVisitScheduleRuleInput {
  weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  start_time: TimeString;
  end_time: TimeString;
  slot_minutes?: 30 | 45 | 60 | 90 | 120;
  is_active?: boolean;
}

export interface PropertySaveAdminCommercialSettingsPayload {
  reservation_settings: {
    is_reservable: boolean;
    reservation_amount?: number | null;
  };
}

export interface PropertySetStatusResponse {
  property_id: UUID;
  publication_status: PublicationStatus;
  availability_status: AvailabilityStatus;
  updated_at: ISODateTimeString;
}

export interface PropertyDeletePreparationStorageObject {
  media_id: number;
  bucket_id: string;
  storage_path: string;
  media_kind: MediaKind;
}

export interface PropertyDeletePreparationUnmanagedMedia {
  media_id: number;
  public_url: string | null;
  media_kind: MediaKind;
}

export interface PropertyDeletePreparationResponse {
  property_id: UUID;
  can_delete: boolean;
  storage_objects: PropertyDeletePreparationStorageObject[];
  unmanaged_media: PropertyDeletePreparationUnmanagedMedia[];
}

export interface PropertyDeleteConfirmResponse {
  property_id: UUID;
  deleted: boolean;
}

// -----------------------------------------------------------------------------
// SECTION 12 — CATÁLOGOS / OPTIONS REUTILIZABLES
// -----------------------------------------------------------------------------

export interface PropertyTypeOption {
  code: PropertyTypeCode;
  label: string;
  family: PropertyFamilyCode;
}

export interface PropertySubtypeOption<T extends PropertyTypeCode = PropertyTypeCode> {
  type: T;
  code: PropertySubtypeCodeByType<T>;
  label: string;
}

export const PROPERTY_TYPE_OPTIONS: PropertyTypeOption[] = (
  Object.entries(PROPERTY_TYPE_CATALOG) as Array<
    [PropertyTypeCode, (typeof PROPERTY_TYPE_CATALOG)[PropertyTypeCode]]
  >
).map(([code, config]) => ({
  code,
  label: config.label,
  family: config.family,
}));

export const PROPERTY_SUBTYPE_OPTIONS_BY_TYPE: {
  [K in PropertyTypeCode]: Array<PropertySubtypeOption<K>>;
} = Object.fromEntries(
  (Object.entries(PROPERTY_TYPE_CATALOG) as Array<
    [PropertyTypeCode, (typeof PROPERTY_TYPE_CATALOG)[PropertyTypeCode]]
  >).map(([type, config]) => [
    type,
    config.subtypes.map((code) => ({
      type,
      code,
      label: PROPERTY_SUBTYPE_LABELS[code],
    })),
  ])
) as {
  [K in PropertyTypeCode]: Array<PropertySubtypeOption<K>>;
};

// -----------------------------------------------------------------------------
// SECTION 13 — UTILITIES
// -----------------------------------------------------------------------------

/**
 * Traduce cualquier enum a español.
 */
export function label<T extends string>(
  map: Record<T, string>,
  value: T | null | undefined,
  fallback = '—'
): string {
  if (value == null) return fallback;
  return map[value] ?? fallback;
}

/**
 * Formatea un precio en MXN.
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency = 'MXN',
  maximumFractionDigits = 0
): string {
  if (amount == null) return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency,
    maximumFractionDigits,
  }).format(amount);
}

/**
 * Alias semántico para renta.
 */
export function formatRent(
  amount: number | null | undefined,
  currency = 'MXN'
): string {
  return formatCurrency(amount, currency, 0);
}

/**
 * Formatea resumen de specs de una card.
 */
export function formatSpecs(
  p: Pick<PropertyDashboardCardAgent, 'bedrooms' | 'bathrooms' | 'area_m2'>
): string {
  const parts: string[] = [];
  if (p.bedrooms != null) parts.push(`${p.bedrooms} rec`);
  if (p.bathrooms != null) parts.push(`${p.bathrooms} baños`);
  if (p.area_m2 != null) parts.push(`${p.area_m2} m²`);
  return parts.join(' · ') || '—';
}

/**
 * Determina si una propiedad es visible al público.
 */
export function isPubliclyVisible(status: PublicationStatus): boolean {
  return status === 'published';
}

/**
 * Determina si una propiedad puede considerarse rentable/mostrable.
 */
export function isRentable(status: AvailabilityStatus): boolean {
  return status === 'available' || status === 'coming_soon';
}

/**
 * Determina si una card es admin.
 */
export function isAdminCard(
  card: PropertyDashboardCardAgent | PropertyDashboardCardAdmin
): card is PropertyDashboardCardAdmin {
  return 'agent_display_name' in card;
}

/**
 * Devuelve la familia de un tipo.
 */
export function getFamilyByType(type: PropertyTypeCode): PropertyFamilyCode {
  return PROPERTY_TYPE_CATALOG[type].family;
}

/**
 * Devuelve todos los tipos de una familia.
 */
export function getTypesByFamily(family: PropertyFamilyCode): PropertyTypeCode[] {
  return (Object.keys(PROPERTY_TYPE_CATALOG) as PropertyTypeCode[]).filter(
    (type) => PROPERTY_TYPE_CATALOG[type].family === family
  );
}

/**
 * Devuelve los subtipos válidos de un tipo.
 */
export function getSubtypesByType<T extends PropertyTypeCode>(
  type: T
): Array<PropertySubtypeOption<T>> {
  return PROPERTY_SUBTYPE_OPTIONS_BY_TYPE[type];
}

/**
 * Type guard: valida si un subtipo pertenece a un tipo.
 */
export function isSubtypeAllowedForType<T extends PropertyTypeCode>(
  type: T,
  subtype: string | null | undefined
): subtype is PropertySubtypeCodeByType<T> {
  if (!subtype) return false;
  return (PROPERTY_TYPE_CATALOG[type].subtypes as readonly string[]).includes(subtype);
}

/**
 * Devuelve si un rol es operador interno.
 */
export function isInternalOperatorRole(role: UserRole): role is InternalOperatorRole {
  return role === 'admin' || role === 'agent';
}

/**
 * Orden local opcional de cards.
 * Ojo: si ya ordenas en backend, úsalo solo para reorden local.
 */
export function sortDashboardItems<T extends PropertyDashboardCardAgent>(
  items: T[],
  sort: DashboardSortOption
): T[] {
  return [...items].sort((a, b) => {
    switch (sort) {
      case 'price_asc':
        return (a.rent_amount ?? 0) - (b.rent_amount ?? 0);
      case 'price_desc':
        return (b.rent_amount ?? 0) - (a.rent_amount ?? 0);
      case 'score_desc':
        return b.publication_score_pct - a.publication_score_pct;
      case 'views_desc':
        return b.views_count - a.views_count;
      case 'created_desc':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'updated_desc':
      default:
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
    }
  });
}
